import React from 'react'
import { useRouter } from 'next/router'
import InfiniteScroll from 'react-infinite-scroll-component'

import { Shimmer } from '@wartek-id/shimmer'
import { Text } from '@wartek-id/text-v1'

import { EmptyStateWithAction } from 'app/ProfileRevampPage/components/EmptyState'
import Notification from 'app/ProfileRevampPage/components/Notification'
import { useCertificates } from 'app/ProfileRevampPage/hooks/useCertificates'
import { useProfile } from 'app/ProfileRevampPage/hooks/useProfile'
import CertificateCard from 'app/ProfileRevampPage/components/CertificateCard'
import CertificateEndResult from 'app/ProfileRevampPage/components/CertificateEndResult'
import CertificateError from 'app/ProfileRevampPage/components/CertificateError'
import { CERTIFICATE_HELP_ZENDESK_LINK } from 'app/ProfileRevampPage/constants'
import { useSETrackEventCertificates } from 'app/ProfileRevampPage/hooks/useSETrackEventCertificates'
import { useProfileCertificateOnboarding } from 'stores/profile'
import { useProfileDesktopView } from 'app/ProfileRevampPage/hooks/useProfileDesktopView'

const LoadingState = () => {
  return (
    <div className="px-4 pb-4 lg:grid lg:grid-cols-2 lg:gap-4 lg:px-0">
      {Array(3)
        .fill('')
        .map((_, index) => (
          <Shimmer
            key={index}
            className="mb-4 rounded-lg lg:mb-0"
            height={150}
            style={{ width: '100%' }}
          />
        ))}
    </div>
  )
}

interface ICertificateYearDividerProps {
  year: string
}
const CertificateYearDivider = ({ year }: ICertificateYearDividerProps) => {
  return (
    <div className="flex items-center w-full">
      <div className="flex-1 h-[1px] bg-gray-30"></div>
      <Text className="px-6" variant="subheading" color="subdued">
        {year}
      </Text>
      <div className="flex-1 h-[1px] bg-gray-30"></div>
    </div>
  )
}

const SectionCertificate = () => {
  let latestYear = ''
  const { isDesktop } = useProfileDesktopView()
  const [hasScrolled, setScrolled] = React.useState<boolean>(false)
  const { isCurrentUser } = useProfile()
  const { query } = useRouter()
  const { id: profileId } = query
  const {
    data: certificateList,
    meta: certificateMeta,
    fetchNextPage,
    hasNextPage,
    isLoading,
    refetch,
    isFetchingNextPage,
  } = useCertificates(profileId as string)
  const hasCertificates = React.useMemo(
    () => certificateList.length > 0,
    [certificateList.length]
  )
  const profileCertificateOnboardingStore = useProfileCertificateOnboarding()
  const { SETrackCertificateList, SETrackCertificateError } =
    useSETrackEventCertificates()

  const clickLoadMoreHandler = React.useCallback((): void => {
    const currentPageIndex =
      (certificateMeta?.offset ?? 0) / (certificateMeta?.limit ?? 10)
    refetch({ refetchPage: (page, index) => index === currentPageIndex })
  }, [certificateMeta, refetch])

  React.useEffect(() => {
    if (!isLoading) {
      SETrackCertificateList('LOAD_SERTIFIKAT_SECTION', {
        num_components: certificateMeta?.total ?? 0,
      })

      if (!profileCertificateOnboardingStore.isRendered && hasCertificates) {
        SETrackCertificateList('LOAD_TOOLTIP_SERTIFIKAT_SECTION')
        profileCertificateOnboardingStore.setRendered()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading])

  React.useEffect(() => {
    if (!hasScrolled && certificateMeta?.offset === 0 && isFetchingNextPage) {
      SETrackCertificateList('SCROLL_CERTIFICATE_LIST', {
        num_components: certificateMeta?.total ?? 0,
      })
      setScrolled(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [certificateMeta?.offset, hasScrolled, isFetchingNextPage])

  React.useEffect(() => {
    if (!isLoading && !hasCertificates) {
      SETrackCertificateError('ERROR_NO_RESULTS')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCertificates, isLoading])

  React.useEffect(() => {
    if (certificateMeta?.isNextPageError) {
      SETrackCertificateError('ERROR_SYSTEM_ERROR')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [certificateMeta?.isNextPageError])

  return (
    <>
      {isCurrentUser ? (
        <Notification
          className={isDesktop ? 'bg-surface-subdued' : undefined}
          data-testid="profile-notification-self-view"
        >
          Rekan guru Anda bisa melihat sertifikat Anda, tapi tidak bisa melihat
          detil atau mengunduh.
        </Notification>
      ) : null}

      {isLoading && <LoadingState />}

      {!isLoading && (
        <div className="pt-4 md:p-6">
          {hasCertificates ? (
            <InfiniteScroll
              className="min-h-[calc(100vh-350px)] !overflow-hidden lg:min-h-[360px]"
              dataLength={certificateList.length}
              next={fetchNextPage}
              hasMore={hasNextPage}
              loader={
                certificateMeta?.isNextPageError ? (
                  <CertificateError onClick={clickLoadMoreHandler} />
                ) : (
                  <LoadingState />
                )
              }
              endMessage={
                certificateMeta?.isNextPageError ? (
                  <CertificateError onClick={clickLoadMoreHandler} />
                ) : isCurrentUser ? (
                  <CertificateEndResult />
                ) : (
                  <div className="pb-8"></div>
                )
              }
            >
              <div className="space-y-4 lg:grid lg:grid-cols-2 lg:gap-4 lg:space-y-0">
                {certificateList.map((certificate, index) => {
                  const currentYear = certificate.date
                    .split(' ')
                    .pop() as string

                  const certificateItem = (
                    <React.Fragment
                      key={`certificate-${certificate?.trainingId}-${certificate?.id}`}
                    >
                      {latestYear !== currentYear && (
                        <div className="px-4 lg:col-span-2 lg:px-0">
                          <CertificateYearDivider year={currentYear} />
                        </div>
                      )}
                      <div className="px-4 last-of-type:pb-4 lg:px-0 lg:last-of-type:pb-0">
                        <CertificateCard
                          showOnboardingTooltip={
                            profileCertificateOnboardingStore.isRendered &&
                            !profileCertificateOnboardingStore.isFinished &&
                            index === 0
                          }
                          index={index}
                          data={certificate}
                          totalRow={certificateMeta?.total ?? 0}
                        />
                      </div>
                    </React.Fragment>
                  )

                  if (latestYear !== currentYear) {
                    latestYear = currentYear
                  }

                  return certificateItem
                })}
              </div>
            </InfiniteScroll>
          ) : (
            <EmptyStateWithAction
              data-testid="profile-empty-state-certificates"
              {...(isCurrentUser
                ? {
                    description: (
                      <>
                        Belum ada sertifikat. Saat ini, kami hanya tampilkan
                        sertifikat dari <b>pelatihan dan waktu tertentu.</b>
                      </>
                    ),
                    actionText: 'Pelajari lebih lanjut',
                    actionPath: CERTIFICATE_HELP_ZENDESK_LINK,
                    onActionButtonClicked: () => {
                      SETrackCertificateError('CLICK_LEARN_MORE')
                    },
                  }
                : {
                    description:
                      'Belum ada sertifikat. Yuk, ajak rekan Anda untuk ikut pelatihan!',
                  })}
              className="mx-4 mb-4 p-4 bg-surface-neutral-subdued rounded-[4px] lg:m-0 lg:rounded-xl"
            />
          )}
        </div>
      )}
    </>
  )
}

export default SectionCertificate
